/**
 * Workload COMMERCIAL-assumptions catalog — the developer-economics view of
 * each `WorkloadProfile`, surfaced in the intent Workload step so the person
 * choosing a template can see the deal shape behind it.
 *
 * WHY THIS LIVES APART FROM `workloadProfile.ts`:
 * every field on `WorkloadProfileEntry` maps to a LIVE behaviour surface
 * (scoring weights, data-provider gating, prompt strings). The figures below
 * are REFERENCE / DISPLAY assumptions only — they must NOT feed scoring, LCOE,
 * or any report number (those come from a site's real `FinancialAssumptions`).
 * Keeping them in a separate module makes that boundary explicit and prevents a
 * future call site from accidentally wiring a headline dev-fee into the math.
 *
 * ⚠️ INDICATIVE PLANNING ASSUMPTIONS — order-of-magnitude, developer-facing,
 * framed for a US greenfield play as of 2026:
 *   - The STRUCTURAL columns (scale, end-customer, offtake, revenue model,
 *     load) are industry-typical and reasonably stable.
 *   - `devFeePerMw` encodes Deep Atomic's OWN origination thesis. The three
 *     AI/colo rows are seeded to William's stated ~$300k/MW anchor (2026-07-08)
 *     so the headline reflects the hypothesis under test; the honest
 *     risk-adjusted divergence lives in `developerNote`. This is the column
 *     most worth confirming against a live deal model before it is quoted.
 *
 * To adjust the thesis, edit the `devFeePerMw` / `developerNote` values below —
 * the table and any downstream reader pick the change up with no other edits.
 */
import type { WorkloadProfile } from './workloadProfile';

export interface WorkloadCommercialAssumption {
  /** Achievable SINGLE-SITE / single-campus scale (not an aggregate fleet). */
  typicalScale: string;
  /** Who ultimately pays — the offtaker / anchor-tenant archetype. */
  endCustomer: string;
  /** Contract / offtake structure that de-risks (or doesn't) the build. */
  offtake: string;
  /** How the developer realises value, and when the dev fee crystallises. */
  revenueModel: string;
  /** Indicative developer fee, $/MW of entitled capacity (Deep Atomic thesis). */
  devFeePerMw: string;
  /** Load shape — drives capacity factor and capital intensity per MW. */
  loadProfile: string;
  /**
   * One honest line on where this template's headline economics CONVERGE with
   * or DIVERGE from the others — specifically the counterparty, achievable
   * single-site scale, and lease-up / absorption risk a capital partner prices.
   */
  developerNote: string;
}

/**
 * Keyed by every `WorkloadProfile` — the `Record` type makes the map exhaustive
 * at compile time, so adding a workload profile without its commercial row is a
 * build error rather than an undefined table cell.
 */
export const WORKLOAD_COMMERCIAL_ASSUMPTIONS: Record<WorkloadProfile, WorkloadCommercialAssumption> = {
  'ai-training': {
    typicalScale: '100 MW – 1 GW+, single campus',
    endCustomer: 'One hyperscaler / frontier lab (IG credit)',
    offtake: 'Build-to-suit / pre-lease, 10–15 yr triple-net',
    revenueModel: 'Powered-shell or turnkey lease; fee at pre-lease → stabilisation',
    devFeePerMw: '$300k/MW',
    loadProfile: 'Sustained ~24×7 near nameplate',
    developerNote:
      'One creditworthy anchor de-risks the whole campus, so a capital partner pays the full dev fee — and training is natively hyperscale, so 1 GW on a single site is realistic.',
  },
  'ai-inference': {
    typicalScale: '5–100 MW per metro (1 GW = a fleet)',
    endCustomer: 'Hyperscaler + enterprise, latency-bound',
    offtake: 'Pre-lease + merchant capacity across many metros',
    revenueModel: 'Lease / capacity sales per metro; fee realised site-by-site',
    devFeePerMw: '$300k/MW',
    loadProfile: 'Spiky, diurnal request / response',
    developerNote:
      'Same fee per MW, but latency pins sites to population centres — reaching 1 GW means originating many mid-size sites, not one campus, so origination effort per GW is materially higher.',
  },
  colocation: {
    typicalScale: '10–100 MW retail; 100 MW – 1 GW+ wholesale',
    endCustomer: 'Many tenants (retail) / a few large (wholesale)',
    offtake: 'Anchor wholesale pre-lease + retail lease-up over time',
    revenueModel: 'Recurring $/kW/month across a tenant stack; fee at stabilised occupancy',
    devFeePerMw: '$300k/MW',
    loadProfile: 'Mixed tenant loads, ramps with occupancy',
    developerNote:
      'Reaches AI-training parity only in the WHOLESALE model with anchor pre-leases. Speculative retail lease-up adds absorption risk a capital partner discounts from the fee — same headline $/MW, lower risk-adjusted value.',
  },
  hpc: {
    typicalScale: '20–200 MW, research campus',
    endCustomer: 'Government lab / consortium / academia',
    offtake: 'Grant- / appropriation-funded procurement',
    revenueModel: 'Design-build / EPC fee; sometimes host-and-operate',
    devFeePerMw: '$150–250k/MW',
    loadProfile: 'Batch-scheduled, time-of-use tolerant',
    developerNote:
      'Public-budget counterparty caps the fee; batch load lets siting chase cheap off-peak power over latency.',
  },
  enterprise: {
    typicalScale: '5–50 MW, corporate / private cloud',
    endCustomer: 'Single corporate owner-occupier',
    offtake: 'Owner-occupied or single-tenant lease',
    revenueModel: 'Design-build fee or sale-leaseback',
    devFeePerMw: '$150–250k/MW',
    loadProfile: 'Steady commodity load, long refresh cycle',
    developerNote:
      'Tracks conventional real-estate development, not the AI premium; low density means more shell per MW of IT.',
  },
  edge: {
    typicalScale: '<5 MW per node (fleet aggregates up)',
    endCustomer: 'CDN / telco / content platform',
    offtake: 'Master lease across a node fleet',
    revenueModel: 'Per-node lease; fee realised across the rollout',
    devFeePerMw: '$200–400k/MW',
    loadProfile: 'Low, latency-driven, often unmanned',
    developerNote:
      'Tiny sites carry disproportionate soft cost per MW; value is in fleet coverage, not any single node.',
  },
  mixed: {
    typicalScale: 'Undetermined (defaults enterprise-grade)',
    endCustomer: 'Not yet decided',
    offtake: 'TBD — the dominant uncertainty',
    revenueModel: 'TBD',
    devFeePerMw: '—',
    loadProfile: 'Assumed enterprise-grade until firmed up',
    developerNote:
      'Firm up the workload before underwriting — it swings scale, counterparty and fee more than any other single input.',
  },
};

/** Convenience lookup with the same throw-on-miss contract as the technical catalog. */
export function getWorkloadCommercialAssumption(id: WorkloadProfile): WorkloadCommercialAssumption {
  const found = WORKLOAD_COMMERCIAL_ASSUMPTIONS[id];
  if (!found) throw new Error(`No commercial assumption for workloadProfile: ${id}`);
  return found;
}
